import { useState, useEffect } from 'react';
import db from '@/db';
import { useAuth } from '@/context/AuthContext';
import { openCoffeeCommand } from '@/lib/commands';
import { printCommandTicket } from '@/lib/printSmart';
import { formatCurrency } from '@/lib/formatters';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Coffee, Printer } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Botão + modal para abrir comanda de Café da Manhã (valor fixo, sem pesagem).
 * Reutilizável em /garcom e /caixa.
 */
export default function CoffeeComboButton({ variant = 'full', onDone }) {
  const { operator } = useAuth();
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState('');
  const [price, setPrice] = useState(15);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      db.config.get('coffeeComboPrice').then((c) => {
        if (c?.value) setPrice(c.value);
      });
    }
  }, [open]);

  const handleOpen = async () => {
    if (!number) return toast.error('Informe o número da comanda');
    setSaving(true);
    try {
      const cmd = await openCoffeeCommand(Number(number), operator?.id);
      toast.success(`Comanda #${cmd.number} aberta com Café da Manhã (${formatCurrency(price)})`);

      // Tenta imprimir direto (modo Windows=HTML ou WebSerial=ESC/POS).
      const fakeEntry = { weight: 0, pricePerKg: 0, subtotal: 0 };
      try {
        const r = await printCommandTicket(cmd, fakeEntry);
        toast.success('Cupom enviado para impressão', {
          description: r.mode === 'windows' ? 'Confira o diálogo do sistema' : 'Via Jetway (ESC/POS)',
        });
      } catch {
        toast.message('Comanda salva. Impressora offline.', {
          description: 'Use /caixa para preview e impressão posterior.',
        });
      }

      setNumber('');
      setOpen(false);
      if (onDone) onDone(cmd);
    } catch (err) {
      toast.error(err.message || 'Erro ao abrir comanda');
    } finally {
      setSaving(false);
    }
  };

  const btnCls = variant === 'compact'
    ? 'flex items-center gap-2 h-10 px-4 rounded-lg bg-amber-600 text-white text-sm font-bold hover:bg-amber-700 active:translate-y-[1px] transition-all'
    : 'w-full flex items-center justify-center gap-2 h-14 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-lg hover:from-amber-600 hover:to-orange-600 active:translate-y-[1px] transition-all shadow-[0_4px_12px_rgba(245,158,11,0.25)]';

  return (
    <>
      <button data-testid="coffee-combo-btn" onClick={() => setOpen(true)} className={btnCls}>
        <Coffee size={variant === 'compact' ? 14 : 20} strokeWidth={2} />
        Abrir Comanda Café
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="coffee-combo-dialog" className="max-w-sm bg-white border border-slate-200 rounded-2xl">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center">
                <Coffee size={18} strokeWidth={2} />
              </div>
              <DialogTitle className="font-heading text-xl font-bold">Abrir Comanda Café</DialogTitle>
            </div>
            <DialogDescription>Lança o combo do café da manhã com valor fixo, sem pesagem. Imprime automaticamente.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-1 block">Número da Comanda</label>
              <Input
                data-testid="coffee-number-input"
                type="number"
                min="1"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="Ex: 42"
                className="h-14 border border-slate-200 rounded-xl text-2xl font-mono text-center font-bold"
                autoFocus
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Valor do Combo</p>
                <p className="font-mono text-2xl font-black text-amber-800 tracking-tighter">{formatCurrency(price)}</p>
              </div>
              <p className="text-xs text-amber-700">configurável em /config</p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <button onClick={() => setOpen(false)} className="px-5 h-10 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">Cancelar</button>
            <button
              data-testid="coffee-confirm-btn"
              onClick={handleOpen}
              disabled={!number || saving}
              className="px-5 h-10 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold flex items-center gap-2 hover:from-amber-600 hover:to-orange-600 disabled:opacity-40"
            >
              <Printer size={14} /> {saving ? 'Abrindo...' : 'Abrir e Imprimir'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
